import React, { useState, useEffect, useCallback } from 'react';
import api, { API_URL } from '../utils/api';

const typeLabels = {
  'undang-undang': 'Undang-Undang', 'peraturan-pemerintah': 'PP',
  'perpres': 'Perpres', 'permen': 'Permen', 'perka-lkpp': 'Perlem LKPP',
  'perdirjen': 'Perdirjen', 'se': 'Surat Edaran', 'pedoman': 'Pedoman', 'lainnya': 'Lainnya'
};
const statusColors = { berlaku: 'badge-green', dicabut: 'badge-red', diubah: 'badge-yellow' };

const RegulationsPage = () => {
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedReg, setSelectedReg] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [viewMode, setViewMode] = useState('info'); // 'info' or 'document'

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get('/regulations', { params });
      setRegulations(data.regulations);
      setPagination(data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterType, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const openRegulation = (reg) => {
    setSelectedReg(reg);
    setViewMode('info');
  };

  const getViewUrl = (reg) => `${API_URL}/regulations/${reg.id}/view`;
  const getDownloadUrl = (reg) => `${API_URL}/regulations/${reg.id}/download`;

  // Detail view
  if (selectedReg) {
    return (
      <div className="fade-in">
        <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedReg(null); setViewMode('info'); }} style={{ marginBottom: 20 }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
          Kembali ke Daftar Peraturan
        </button>

        {/* Header */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <span className="badge badge-orange">{typeLabels[selectedReg.type]}</span>
                <span className={`badge ${statusColors[selectedReg.status]}`}>{selectedReg.status}</span>
                <span className="badge badge-gray">{selectedReg.year}</span>
                {selectedReg.file_path && <span className="badge badge-blue">📄 Dokumen tersedia</span>}
              </div>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.5, marginBottom: 4 }}>{selectedReg.title}</h2>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>{selectedReg.number}</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selectedReg.file_path && (
                <>
                  <button
                    className={`btn btn-sm ${viewMode === 'document' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setViewMode(viewMode === 'document' ? 'info' : 'document')}
                  >
                    <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {viewMode === 'document' ? 'Lihat Info' : 'Baca Dokumen'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => window.open(getDownloadUrl(selectedReg))}>
                    <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                    Download
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Document viewer mode */}
        {viewMode === 'document' && selectedReg.file_path && (
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{
              padding: '12px 20px',
              background: 'var(--bg-tertiary)',
              borderBottom: '1px solid var(--border-secondary)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="var(--accent-primary)" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Dokumen: {selectedReg.number}
                </span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => window.open(getViewUrl(selectedReg), '_blank')}>
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                Buka di Tab Baru
              </button>
            </div>
            <iframe
              src={getViewUrl(selectedReg)}
              title={`Dokumen ${selectedReg.number}`}
              style={{
                width: '100%',
                height: 'calc(100vh - 320px)',
                minHeight: 500,
                border: 'none',
                display: 'block'
              }}
            />
          </div>
        )}

        {/* Info mode */}
        {viewMode === 'info' && (
          <>
            {selectedReg.description && (
              <div className="card" style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, marginBottom: 10, color: 'var(--text-primary)' }}>Deskripsi</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                  {selectedReg.description}
                </p>
              </div>
            )}

            {selectedReg.content && (
              <div className="card" style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, marginBottom: 10, color: 'var(--text-primary)' }}>Isi Peraturan</h3>
                <div
                  style={{
                    background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)',
                    padding: 20, fontSize: '0.875rem', lineHeight: 1.8,
                    color: 'var(--text-primary)'
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedReg.content }}
                  className="content-html"
                />
              </div>
            )}

            {/* Quick document preview if file exists */}
            {selectedReg.file_path && (
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 'var(--radius-md)',
                      background: 'rgba(239,68,68,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.25rem'
                    }}>📄</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Dokumen Peraturan</div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                        {selectedReg.file_path?.split('.').pop()?.toUpperCase()} • {selectedReg.file_size ? `${(selectedReg.file_size / 1024).toFixed(0)} KB` : 'Tersedia untuk dibaca'}
                        {selectedReg.download_count > 0 && ` • ${selectedReg.download_count}x diunduh`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => setViewMode('document')}>
                      <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      Baca Dokumen
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => window.open(getDownloadUrl(selectedReg))}>
                      <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <style>{`
          .content-html h2 { font-size: 1.25rem; font-weight: 700; margin: 24px 0 12px; color: var(--text-primary); }
          .content-html h3 { font-size: 1.0625rem; font-weight: 600; margin: 20px 0 10px; color: var(--text-primary); }
          .content-html p { margin-bottom: 12px; }
          .content-html ul, .content-html ol { padding-left: 24px; margin-bottom: 12px; }
          .content-html li { margin-bottom: 6px; }
          .content-html strong { font-weight: 600; color: var(--text-accent); }
          .content-html table { width: 100%; border-collapse: collapse; margin: 16px 0; }
          .content-html th, .content-html td { padding: 10px 14px; border: 1px solid var(--border-primary); text-align: left; }
          .content-html th { background: var(--bg-tertiary); font-weight: 600; }
        `}</style>
      </div>
    );
  }

  // List view
  return (
    <div className="fade-in">
      {/* Search & Filters */}
      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '1 1 300px' }}>
            <label className="form-label">Cari Peraturan</label>
            <input className="form-input" placeholder="Cari berdasarkan judul, nomor..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: '0 1 180px' }}>
            <label className="form-label">Jenis</label>
            <select className="form-select" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}>
              <option value="">Semua Jenis</option>
              {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: '0 1 150px' }}>
            <label className="form-label">Status</label>
            <select className="form-select" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">Semua</option>
              <option value="berlaku">Berlaku</option>
              <option value="dicabut">Dicabut</option>
              <option value="diubah">Diubah</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary">
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
            Cari
          </button>
        </form>
      </div>

      {/* Results */}
      {loading ? (
        <div className="loading-screen"><div className="spinner" /><span>Memuat peraturan...</span></div>
      ) : regulations.length === 0 ? (
        <div className="empty-state">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
          <p>Tidak ada peraturan ditemukan</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {regulations.map((reg) => (
            <div key={reg.id} className="card" style={{ cursor: 'pointer', padding: '20px 24px' }} onClick={() => openRegulation(reg)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className="badge badge-orange">{typeLabels[reg.type] || reg.type}</span>
                    <span className={`badge ${statusColors[reg.status]}`}>{reg.status}</span>
                    <span className="badge badge-gray">{reg.year}</span>
                    {reg.file_path && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        📄 Dokumen
                      </span>
                    )}
                  </div>
                  <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.5, marginBottom: 4 }}>
                    {reg.title}
                  </h3>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>{reg.number}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {reg.file_path && (
                    <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); window.open(getDownloadUrl(reg)); }}>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                      Unduh
                    </button>
                  )}
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="var(--text-tertiary)" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&laquo;</button>
              {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>&raquo;</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegulationsPage;
